// One clock for every animated character on the page (4 Nov 2026): a single requestAnimationFrame loop that calls each
// subscriber with the time, plus one-shot timers on the same loop. It runs only while something is subscribed, and
// stands still while the page is hidden: its time leaves out the hidden spell, so an act in progress picks up where it
// was rather than jumping to its end.

type Tick = (now: number) => void;

const ticks = new Set<Tick>();
let raf = 0;
let hiddenSince: number | undefined;
let paused = 0;
let listening = false;

const raw = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
const hasRaf = () => typeof requestAnimationFrame === 'function';

/** The clock's time in ms: the page's time less every spell it spent hidden. */
export function clockNow(): number {
  return raw() - paused - (hiddenSince !== undefined ? raw() - hiddenSince : 0);
}

function frame() {
  raf = 0;
  if (!ticks.size || hidden()) return;
  const now = clockNow();
  for (const t of [...ticks]) {
    try { t(now); } catch { ticks.delete(t); }
  }
  if (ticks.size && !raf) raf = requestAnimationFrame(frame);
}

function start() {
  if (!raf && ticks.size && !hidden() && hasRaf()) raf = requestAnimationFrame(frame);
}

function stop() {
  if (raf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
  raf = 0;
}

function onVisibility() {
  if (hidden()) { if (hiddenSince === undefined) hiddenSince = raw(); stop(); }
  else {
    if (hiddenSince !== undefined) { paused += raw() - hiddenSince; hiddenSince = undefined; }
    start();
  }
}

function listen() {
  if (listening || typeof document === 'undefined') return;
  listening = true;
  document.addEventListener('visibilitychange', onVisibility);
  if (hidden()) hiddenSince = raw();
}

/** Calls `fn` with the clock's time on every frame until the returned function is called. */
export function onFrame(fn: Tick): () => void {
  listen();
  ticks.add(fn);
  start();
  return () => {
    ticks.delete(fn);
    if (!ticks.size) stop();
  };
}

/** Calls `fn` once, `ms` of clock time from now (hidden time does not count). Returns a cancel. */
export function after(ms: number, fn: () => void): () => void {
  const at = clockNow() + ms;
  const off = onFrame((now) => { if (now >= at) { off(); fn(); } });
  return off;
}

/** True while the loop is scheduled (tests). */
export function clockRunning(): boolean {
  return raf !== 0;
}

/** How many subscribers the loop has (tests). */
export function clockSubscribers(): number {
  return ticks.size;
}
